import { getCareerColorHex, tintWithWhite } from '@/utils/careers';
import type { ShiftCalendarEvent, CalendarHoliday } from '@/api/shifts-calendar-events';
import type { PatientScheduleBlock } from '@/api/patient-schedule-blocks';
import { blockDaySegments } from '@/utils/schedule-blocks';

export interface CalendarV2Event {
  shiftId?: number;
  start: string;
  end: string;
  title: string;
  content: string;
  class: string;
  schedule?: number;
  allDay?: boolean;
  continued: boolean;
  compact: boolean;
  hasReports: boolean;
  hasIncidents: boolean;
  colorHex: string;
  colorTint: string;
  // Patient schedule block rendered as a regular vue-cal event so it shares
  // the overlap cascade with shifts and stays visible next to them.
  blockId?: number;
  draggable?: boolean;
  resizable?: boolean;
  deletable?: boolean;
  // Description of the block a shift sits inside (flag icon on the event).
  scheduleBlockDescription?: string;
}

export interface BuildCalendarEventsOptions {
  labelBy: 'patient' | 'caregiver';
  rangeStart: string;
  rangeEnd: string;
  scheduleBy?: 'patient' | 'caregiver';
}

interface EventSegment {
  date: string;
  startTime: string;
  endTime: string;
  continued: boolean;
}

const FIRST_MINUTE = '00:00';
// vue-cal clamps midnight ends internally; an explicit 23:59 keeps segments
// inside their own day column.
const LAST_MINUTE = '23:59';
const ISO_DATE_LENGTH = 10;
const SUNDAY_TO_MONDAY_OFFSET = -6;
const DATE_PART_DIGITS = 2;
const MINUTES_PER_HOUR = 60;
// Below this duration the block only fits one line, so the event shows the
// person's name instead of the time range.
const COMPACT_THRESHOLD_MINUTES = 60;
// Fraction of the career color kept in the block background, mimicking the
// tonal v-chip look.
const EVENT_TINT_RATIO = 0.15;

export function addDays(date: string, days: number): string {
  const [year, month, day] = date.split('-').map(Number);

  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, ISO_DATE_LENGTH);
}

export function startOfWeek(date: string): string {
  const [year, month, day] = date.split('-').map(Number);
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();

  return addDays(date, weekday === 0 ? SUNDAY_TO_MONDAY_OFFSET : 1 - weekday);
}

export function dateToIso(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(DATE_PART_DIGITS, '0');
  const day = String(date.getDate()).padStart(DATE_PART_DIGITS, '0');

  return `${date.getFullYear()}-${month}-${day}`;
}

// Browser-local date: near midnight this can differ from the company time zone
// that positions the events (acceptable for the navigation anchor only).
export function todayIso(): string {
  return dateToIso(new Date());
}

function crossesMidnight(shift: ShiftCalendarEvent): boolean {
  return shift.endTime <= shift.startTime;
}

function segmentsFor(shift: ShiftCalendarEvent): EventSegment[] {
  if (!crossesMidnight(shift)) {
    return [{
      date: shift.date,
      startTime: shift.startTime,
      endTime: shift.endTime,
      continued: false,
    }];
  }

  const segments: EventSegment[] = [{
    date: shift.date,
    startTime: shift.startTime,
    endTime: LAST_MINUTE,
    continued: false,
  }];

  if (shift.endTime !== FIRST_MINUTE) {
    segments.push({
      date: addDays(shift.date, 1),
      startTime: FIRST_MINUTE,
      endTime: shift.endTime,
      continued: true,
    });
  }

  return segments;
}

function minutesOf(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);

  return (hours * MINUTES_PER_HOUR) + minutes;
}

function scheduleFor(shift: ShiftCalendarEvent, options: BuildCalendarEventsOptions) {
  if (options.scheduleBy === 'caregiver') return shift.caregiverId;
  if (options.scheduleBy === 'patient') return shift.patientId;

  return undefined;
}

function buildEvent(
  shift: ShiftCalendarEvent,
  segment: EventSegment,
  options: BuildCalendarEventsOptions,
): CalendarV2Event {
  const colorHex = getCareerColorHex(shift.caregiverCareer ?? '');
  const title = options.labelBy === 'patient' ? shift.patientFullName : shift.caregiverFullName;
  const duration = minutesOf(segment.endTime) - minutesOf(segment.startTime);

  return {
    shiftId: shift.id,
    start: `${segment.date} ${segment.startTime}`,
    end: `${segment.date} ${segment.endTime}`,
    title,
    content: shift.humanTime,
    class: `calendar-v2-event status-${shift.status.color}`,
    schedule: scheduleFor(shift, options),
    continued: segment.continued,
    compact: duration < COMPACT_THRESHOLD_MINUTES,
    hasReports: shift.hasReports,
    hasIncidents: shift.hasIncidents,
    colorHex,
    colorTint: tintWithWhite(colorHex, EVENT_TINT_RATIO),
    scheduleBlockDescription: shift.scheduleBlock?.description,
  };
}

export function buildCalendarEvents(
  shifts: ShiftCalendarEvent[],
  options: BuildCalendarEventsOptions,
): CalendarV2Event[] {
  return shifts.flatMap(shift => (
    segmentsFor(shift)
      .filter(segment => segment.date >= options.rangeStart && segment.date <= options.rangeEnd)
      .map(segment => buildEvent(shift, segment, options))
  ));
}

// All-day green banners; vue-cal renders them in the all-day bar on top of
// each day column. When person columns are active, vue-cal drops events
// without a matching schedule, so the banner is emitted once per column.
export function buildHolidayEvents(
  holidays: CalendarHoliday[],
  scheduleIds?: number[],
): CalendarV2Event[] {
  return holidays.flatMap(holiday => {
    const banner: CalendarV2Event = {
      start: `${holiday.date} ${FIRST_MINUTE}`,
      end: `${holiday.date} ${LAST_MINUTE}`,
      title: holiday.title,
      content: '',
      class: 'calendar-v2-holiday',
      allDay: true,
      continued: false,
      compact: false,
      hasReports: false,
      hasIncidents: false,
      colorHex: '',
      colorTint: '',
    };
    if (!scheduleIds?.length) return [banner];

    return scheduleIds.map(scheduleId => ({ ...banner, schedule: scheduleId }));
  });
}

// Patient schedule blocks as regular events: one per day segment, sharing the
// overlap cascade with shifts so the block stays visible beside a shift even
// when both cover the same time range. Not draggable, and — unlike holidays —
// owned by one patient column when the day view is split by patient.
export function buildScheduleBlockEvents(
  blocks: PatientScheduleBlock[],
  options: { rangeStart: string; rangeEnd: string; scheduleBy?: 'patient' | 'caregiver' },
): CalendarV2Event[] {
  return blocks.flatMap((block) => (
    blockDaySegments(block, options.rangeStart, options.rangeEnd).map((segment) => ({
      blockId: block.id,
      start: `${segment.date} ${segment.startTime}`,
      end: `${segment.date} ${segment.endTime}`,
      title: block.description,
      content: `${segment.startTime}–${segment.endTime}`,
      class: 'calendar-v2-block',
      draggable: false,
      resizable: false,
      deletable: false,
      schedule: options.scheduleBy === 'patient' ? block.patientId : undefined,
      continued: !segment.startsHere,
      compact: false,
      hasReports: false,
      hasIncidents: false,
      colorHex: '',
      colorTint: '',
    }))
  ));
}

function visibleInRange(shift: ShiftCalendarEvent, rangeStart: string, rangeEnd: string) {
  return segmentsFor(shift).some(
    (segment) => segment.date >= rangeStart && segment.date <= rangeEnd,
  );
}

// Distinct people with a visible shift segment in the range, for the daily
// person columns: deduped by id and sorted alphabetically. The response
// window is padded one day back for night-shift spillover, so shifts whose
// segments all fall outside the range must not produce a column.
export function schedulesFromShifts(
  shifts: ShiftCalendarEvent[],
  by: 'patient' | 'caregiver',
  rangeStart: string,
  rangeEnd: string,
): Array<{ id: number; label: string }> {
  const people = new Map<number, string>();
  shifts.filter((shift) => visibleInRange(shift, rangeStart, rangeEnd)).forEach((shift) => {
    if (by === 'caregiver') people.set(shift.caregiverId, shift.caregiverFullName);
    else people.set(shift.patientId, shift.patientFullName);
  });

  return [...people.entries()]
    .map(([id, label]) => ({ id, label }))
    .sort((a, b) => a.label.localeCompare(b.label, 'es'));
}

